import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Plus, Users } from 'lucide-react';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { useTaskStore } from '../../../store/useTaskStore';

export interface AddTaskCoworker {
  employeeId: string;
  name: string;
}

interface AddTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  employeeName: string;
  planningEntryId?: string;
  preSelectedTaskId?: string;
  preStartTime?: string;
  preEndTime?: string;
  /** Other employees with a WorkDay entry on `date` — the task can be planned for them too. */
  coworkers?: AddTaskCoworker[];
  onSave: (taskId: string, startTime: string, endTime: string, coworkerIds: string[]) => void;
}

export function AddTaskDialog({
  isOpen,
  onClose,
  date,
  employeeName,
  planningEntryId,
  preSelectedTaskId,
  preStartTime,
  preEndTime,
  coworkers = [],
  onSave,
}: AddTaskDialogProps) {
  const { t } = useTranslation('planning');
  const { tasks, fetchTasks } = useTaskStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('12:00');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [coworkerIds, setCoworkerIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && tasks.length === 0) {
      fetchTasks();
    }
  }, [isOpen, tasks.length, fetchTasks]);

  // Pre-select task and times when opened via drag & drop
  useEffect(() => {
    if (isOpen) {
      if (preSelectedTaskId) {
        setSelectedTaskId(preSelectedTaskId);
      } else {
        setSelectedTaskId(null);
      }
      setStartTime(preStartTime || '08:00');
      setEndTime(preEndTime || '12:00');
      setSearchTerm('');
      setCoworkerIds([]);
    }
  }, [isOpen, preSelectedTaskId, preStartTime, preEndTime]);

  if (!isOpen) return null;

  const filteredTasks = tasks.filter((task) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      task.name?.toLowerCase().includes(searchLower) ||
      task.objectName?.toLowerCase().includes(searchLower) ||
      task.serviceName?.toLowerCase().includes(searchLower)
    );
  });

  const selectedTask = tasks.find((t) => t.taskId === selectedTaskId);

  const handleSave = () => {
    if (!selectedTaskId) return;

    onSave(selectedTaskId, startTime, endTime, coworkerIds);
    onClose();
    // Reset
    setSelectedTaskId(null);
    setSearchTerm('');
    setStartTime('08:00');
    setEndTime('12:00');
    setCoworkerIds([]);
  };

  const toggleCoworker = (employeeId: string) => {
    setCoworkerIds((current) =>
      current.includes(employeeId)
        ? current.filter((id) => id !== employeeId)
        : [...current, employeeId]
    );
  };

  const calculateDuration = () => {
    if (!startTime || !endTime) return '0h';
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const minutes = (endH * 60 + endM) - (startH * 60 + startM);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Aufgabe zuweisen
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {employeeName} • {new Date(date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Aufgabe suchen
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Nach Aufgabe, Objekt oder Leistung suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Task List */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Verfügbare Aufgaben ({filteredTasks.length})
            </label>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {filteredTasks.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'Keine Aufgaben gefunden' : 'Keine Aufgaben verfügbar'}
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <button
                    key={task.taskId}
                    onClick={() => setSelectedTaskId(task.taskId || null)}
                    className={`
                      w-full p-4 text-left transition-colors
                      ${selectedTaskId === task.taskId
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }
                    `}
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {task.objectName || 'Unbekanntes Objekt'}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {task.serviceName || 'Keine Leistung'}
                    </div>
                    {task.name && (
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {task.name}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Selected Task + Time */}
          {selectedTask && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="font-medium text-gray-900 dark:text-white mb-4">
                Ausgewählte Aufgabe: {selectedTask.objectName} • {selectedTask.serviceName}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Startzeit
                  </label>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Endzeit
                  </label>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                Dauer: <span className="font-semibold">{calculateDuration()}</span>
              </div>
            </div>
          )}

          {/* Weitere Mitarbeiter (FACF-111) */}
          {coworkers.length > 0 && (
            <div>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Users className="w-4 h-4" strokeWidth={2} />
                  {t('addTask.coworkers')}
                </label>
                {coworkerIds.length > 0 && (
                  <span className="text-[12px] text-slate-500 tabular-nums">
                    {t('addTask.coworkersSelected', { count: coworkerIds.length })}
                  </span>
                )}
              </div>
              <p className="text-[12px] text-slate-500 mb-2">{t('addTask.coworkersHint')}</p>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                {coworkers.map((coworker) => (
                  <label
                    key={coworker.employeeId}
                    className="flex items-center gap-3 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-[#00A871]"
                      checked={coworkerIds.includes(coworker.employeeId)}
                      onChange={() => toggleCoworker(coworker.employeeId)}
                    />
                    <span>{coworker.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Info: No Planning Entry */}
          {!planningEntryId && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Hinweis:</strong> Für diesen Tag existiert noch kein Arbeitstag-Eintrag. 
                Beim Speichern wird automatisch ein Arbeitstag erstellt.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={!selectedTaskId}>
            <Plus className="w-4 h-4 mr-2" />
            Aufgabe zuweisen
          </Button>
        </div>
      </div>
    </div>
  );
}

